/**
 * Softphone Coordination Legacy/New
 *
 * SoftphoneCoordinator - Ensures only one softphone (legacy or new) is active at a time
 *
 * This singleton coordinates between:
 * - Legacy PhoneStore (MobX-based)
 * - New SoftphoneProvider (Zustand-based)
 *
 * Prevents dual SIP registrations and ensures clean transitions
 */

import { DEFAULT_IS_NEW_SOFTPHONE_ENABLED } from 'Modules/softphone/constants/softphoneVersion';

type SoftphoneType = 'legacy' | 'new' | null;

export class SoftphoneCoordinator {
  private static instance: SoftphoneCoordinator;
  private activeSoftphone: SoftphoneType = null;
  private isTransitioning = false;

  private constructor() {
    // Coordinator initialized
  }

  static getInstance(): SoftphoneCoordinator {
    if (!SoftphoneCoordinator.instance) {
      SoftphoneCoordinator.instance = new SoftphoneCoordinator();
    }
    return SoftphoneCoordinator.instance;
  }

  /**
   * Check if we can activate a softphone type
   * Returns true if safe to proceed, false if blocked
   */
  canActivate(type: SoftphoneType): boolean {
    if (this.isTransitioning) {
      return false;
    }

    if (this.activeSoftphone === type) {
      return true;
    }

    if (this.activeSoftphone && this.activeSoftphone !== type) {
      return false;
    }

    return true;
  }

  /**
   * Request activation of a softphone type
   * Automatically deactivates the other type if needed
   */
  requestActivation(type: SoftphoneType): boolean {
    if (this.isTransitioning) {
      return false;
    }

    // If same type is already active, nothing to do
    if (this.activeSoftphone === type) {
      return true;
    }

    // If different type is active, deactivate it first
    if (this.activeSoftphone && this.activeSoftphone !== type) {
      this.deactivate(this.activeSoftphone);
    }

    // Mark as transitioning
    this.isTransitioning = true;

    try {
      this.activeSoftphone = type;
      return true;
    } finally {
      this.isTransitioning = false;
    }
  }

  /**
   * Deactivate a specific softphone type
   */
  deactivate(type: SoftphoneType): void {
    if (this.activeSoftphone === type) {
      this.activeSoftphone = null;
    }
  }

  /**
   * Get currently active softphone type
   */
  getActiveSoftphone(): SoftphoneType {
    return this.activeSoftphone;
  }

  /**
   * Check if transitioning between softphones
   */
  isInTransition(): boolean {
    return this.isTransitioning;
  }

  /**
   * Force reset - use only in error recovery scenarios
   */
  forceReset(): void {
    this.isTransitioning = false;
    this.activeSoftphone = null;
  }

  /**
   * Determine which softphone should be active based on user preference
   * Returns the softphone type that should be initialized
   */
  getSoftphoneTypeFromPreference(
    isNewSoftphoneEnabled: boolean
  ): SoftphoneType {
    return isNewSoftphoneEnabled ? 'new' : 'legacy';
  }

  /**
   * Check if legacy softphone should initialize based on preference and coordinator state
   * Returns true if legacy softphone can proceed with initialization
   */
  shouldInitializeLegacy(isNewSoftphoneEnabled: boolean): boolean {
    const preferredType = this.getSoftphoneTypeFromPreference(
      isNewSoftphoneEnabled
    );

    if (preferredType === 'new') {
      return false;
    }

    const canActivate = this.requestActivation('legacy');
    if (!canActivate) {
      return false;
    }

    return true;
  }

  /**
   * Read the active softphone version directly from localStorage (synchronous).
   * This bypasses the Zustand store and reads the persisted value immediately.
   * Use this when you need the value before the store is hydrated.
   *
   * The persisted value mirrors the `communicator-softphone-v2` feature flag.
   * When nothing is persisted yet the flag has not resolved for this browser,
   * so we fall back to the new softphone — the default experience — and let
   * `syncWithFeatureFlag` downgrade to legacy if the flag turns out to be off.
   *
   * @param personId - User ID for per-user data isolation (required)
   * @returns The isNewSoftphoneEnabled value from localStorage, or the default
   *          (`true`) when it is not stored or cannot be read
   *
   * @example
   * // In PhoneStore constructor (before store hydration)
   * const coordinator = SoftphoneCoordinator.getInstance();
   * const isNewSoftphoneEnabled = coordinator.readPreferenceSync(personId);
   */
  readPreferenceSync(personId: number | string): boolean {
    if (!personId) {
      return DEFAULT_IS_NEW_SOFTPHONE_ENABLED;
    }

    const key = `user-${personId}:softphone-coordinator`;

    try {
      const stored = localStorage.getItem(key);
      if (!stored) return DEFAULT_IS_NEW_SOFTPHONE_ENABLED;

      const parsed = JSON.parse(stored) as {
        state?: { isNewSoftphoneEnabled?: boolean };
      };
      return (
        parsed?.state?.isNewSoftphoneEnabled ?? DEFAULT_IS_NEW_SOFTPHONE_ENABLED
      );
    } catch (error) {
      return DEFAULT_IS_NEW_SOFTPHONE_ENABLED;
    }
  }
}
